import DPSEntitySelector from "components/DPSEntitySelector";
import { GlobalContext } from "components/GlobalContext";
import QRCodeReader from "components/QRCodeReader";
import IDealer, { IDealerFilters } from "models/sql/IDealer";
import IFireDepartment, { IFireDepartmentFilters } from "models/sql/IFireDepartment";
import ISalesRep, { ISalesRepFilters } from "models/sql/ISalesRep";
import { useContext, useRef, useState, useEffect } from "react";
import { Button, Row, Col, Form, Card } from "react-bootstrap";
import DealerService from "services/DealerService";
import FireDepartmentService from "services/FireDepartmentService";
import SalesRepService from "services/SalesRepService";
import EquipmentService from "services/EquipmentService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsSpin, faCheck, faWarning } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import IEquipment from "models/sql/IEquipment";
import IProduct from "models/sql/IProduct";
import ProductService from "services/ProductService";
import { addNewFileAttachment, addNewLogEntry } from "utilities/MiscUtilities";
import MiscService from "services/MiscService";
import { capitalizeWord } from "utilities/StringUtility";

type UpdateMode = "idle" | "qa" | "receive" | "ship";
type SubmissionState = "idle" | "submitting" | "success" | "error";

interface IQueueEntry {
    serial: string,
    state: SubmissionState,
    message: string
};

const EquipmentUpdate = () => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const [serials, setSerials] = useState<string[]>([]);
    const serialsRef = useRef(serials);
    serialsRef.current = serials;
    const [dealer, setDealer] = useState<IDealer>();
    const [salesRep, setSalesRep] = useState<ISalesRep>();
    const [fireDepartment, setFireDepartment] = useState<IFireDepartment>();
    const [registrationName, setRegistrationName] = useState<string>("");
    const [registrationEmail, setRegistrationEmail] = useState<string>("");
    const [mode, setMode] = useState<UpdateMode>("idle");
    const [submissionQueue, setSubmissionQueue] = useState<IQueueEntry[]>([]);
    const [runningSubmission, setRunningSubmission] = useState<boolean>(false);
    const [products, setProducts] = useState<IProduct[]>([]);
    const attachPhotoRef = useRef<HTMLInputElement | null>(null);
    const [photo, setPhoto] = useState<string>();
    const [photoUrl, setPhotoUrl] = useState<string>();

    useEffect(() => {
        const getProductsAsync = async () => {
            globalContextRef.current.setLoading(true);
            const accessToken = await globalContextRef.current.getAccessTokenAsync();
            const productResult = await ProductService.GetAllAsync(accessToken, 0, 999, {});

            if (productResult && productResult.records) {
                setProducts(productResult.records);
            }

            globalContextRef.current.setLoading(false);
        };
        getProductsAsync();
    }, []);

    const onEquipmentScanSuccess = (decodedText: string, decodedResult: any) => {
        console.log("Scan Success", decodedText);
        if (decodedText) {
            const serialPortion = decodedText.split("/").slice(-1)[0].toLowerCase();
            console.log("Serial is " + serialPortion);

            if (serialsRef.current.indexOf(serialPortion) === -1) {
                setSerials([...serialsRef.current, serialPortion]);
            }
        }
    };

    const doSubissionsAsync = async () => {
        if (serials.length > 0) {
            globalContextRef.current.showModal({
                content:
                    <>Are you sure you want to update these {serials.filter(s => !!s)?.length ?? "0"} serial(s) as <strong>{mode === "qa" ? "QA PASSED" : mode === "receive" ? "RECEIVED" : mode === "ship" ? "SHIPPED" : "UNKNOWN"}</strong>?</>,
                title: `Equipment ${mode === "qa" ? "QA" : mode === "receive" ? "RECEIVING" : mode === "ship" ? "SHIPPING" : "UNKNOWN"} Update`,
                okCallback:
                    async (data) => {
                        const newQueue = serials.filter(s => !!s).map(s => ({ serial: s, state: "idle" as SubmissionState, message: "" }));
                        setSubmissionQueue(newQueue);
                        runSubmissionQueueAsync(newQueue);
                    },
                cancelCallback: () => { }
            });
        }
    };

    const runSubmissionQueueAsync = async (newQueue: IQueueEntry[]) => {
        setRunningSubmission(true);
        const accessToken = await globalContextRef.current.getAccessTokenAsync();
        for (const submission of newQueue) {
            const { serial } = submission;

            setSubmissionQueue(prevQueue =>
                prevQueue.map(item =>
                    item.serial === serial
                        ? { ...item, state: "submitting", message: "" }
                        : item
                )
            );

            let existingEquipment: IEquipment | undefined | null = (await EquipmentService.GetBySerialAsync(accessToken, serial))?.record;
            let saveEntry = true;
            let entryMessage = "";
            // photoUrl
            if (!existingEquipment) {
                existingEquipment = {
                    equipmentId: -1,
                    serialNumber: serial,
                    dealerId: dealer?.dealerId,
                    fireDepartmentId: fireDepartment?.fireDepartmentId,
                    productId: products.find(p => p.serialPrefix.toLowerCase() === serial.substring(0, 3).toLowerCase())?.productId,
                    registrationEmail: registrationEmail,
                    registrationName: registrationName,
                    region: fireDepartment?.hqCountry === "United State" ? "United States" : "International",
                    country: fireDepartment?.hqCountry,
                    city: fireDepartment?.hqcity,
                    departmentName: fireDepartment?.fireDeptName,
                    salesRepId: salesRep?.salesRepId,
                    address1: fireDepartment?.hqaddr1,
                    address2: fireDepartment?.hqaddr2,
                    state: fireDepartment?.hqstate,
                    postalCode: fireDepartment?.hqzip
                };
            }
            else {
                if (registrationEmail) {
                    existingEquipment.registrationEmail = registrationEmail;
                }

                if (registrationName) {
                    existingEquipment.registrationName = registrationName;
                }

                if (dealer) {
                    existingEquipment.dealerId = dealer.dealerId;
                }

                if (salesRep) {
                    existingEquipment.salesRepId = salesRep.salesRepId;
                }

                if (fireDepartment) {
                    existingEquipment.fireDepartmentId = fireDepartment?.fireDepartmentId;
                    existingEquipment.region = fireDepartment?.hqCountry === "United State" ? "United States" : "International";
                    existingEquipment.country = fireDepartment?.hqCountry;
                    existingEquipment.city = fireDepartment?.hqcity;
                    existingEquipment.departmentName = fireDepartment?.fireDeptName;
                    existingEquipment.address1 = fireDepartment?.hqaddr1;
                    existingEquipment.address2 = fireDepartment?.hqaddr2;
                    existingEquipment.state = fireDepartment?.hqstate;
                    existingEquipment.postalCode = fireDepartment?.hqzip;
                }
            }

            if (mode === "qa") {
                if (existingEquipment.qaCompleteDate) {
                    saveEntry = false;
                    entryMessage = "Already QA'd";
                }

                if (existingEquipment.receivedDate) {
                    saveEntry = false;
                    entryMessage = "Already received";
                }

                if (existingEquipment.shippedDate) {
                    saveEntry = false;
                    entryMessage = "Already shipped";
                }

                existingEquipment.qaCompleteDate = (new Date()).toISOString();
            }
            else if (mode === "receive") {
                if (existingEquipment.receivedDate) {
                    saveEntry = false;
                    entryMessage = "Already received";
                }

                if (existingEquipment.shippedDate) {
                    saveEntry = false;
                    entryMessage = "Already shipped";
                }

                existingEquipment.receivedDate = (new Date()).toISOString();
            }
            else if (mode === "ship") {
                if (existingEquipment.shippedDate) {
                    saveEntry = false;
                    entryMessage = "Already shipped";
                }

                existingEquipment.shippedDate = (new Date()).toISOString();
            }

            existingEquipment =
                addNewLogEntry(
                    (new Date()),
                    `${globalContext.userName} updated to ${mode === "qa" ? "QA PASSED" : mode === "receive" ? "RECEIVED" : mode === "ship" ? "SHIPPED" : "UNKNOWN"} using the tool`,
                    globalContext.userEmail,
                    101,
                    existingEquipment)

            if (photoUrl) {
                existingEquipment =
                    addNewFileAttachment(
                        (new Date()),
                        photoUrl,
                        globalContext.userName,
                        capitalizeWord(mode) + " Tool Photo",
                        existingEquipment
                    )
            }

            if (saveEntry) {
                await EquipmentService.CreateOrUpdateAsync(accessToken, existingEquipment);
            }

            setSubmissionQueue(prevQueue =>
                prevQueue.map(item =>
                    item.serial === serial
                        ? { ...item, state: saveEntry ? "success" : "error", message: entryMessage }
                        : item
                )
            );
        }

        setRunningSubmission(false);
    };

    const resetAll = () => {
        setSubmissionQueue([]);
        setSerials([]);
        setDealer(undefined);
        setSalesRep(undefined);
        setFireDepartment(undefined);
        setRegistrationName("");
        setRegistrationEmail("");
        setPhoto(undefined);
        setPhotoUrl(undefined);
    };

    const startPhotoSubmission = () => {
        attachPhotoRef.current?.click();
    };

    const attachPhotoCompleteAsync = async (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        event.preventDefault();
        var file = event?.currentTarget?.files?.[0];

        if (file) {
            const formData = new FormData();
            const fileSize = file.size;
            const megs = fileSize / 1048576;

            if (megs > 25) {
                alert("The file you're trying to attach is too large");
                return false;
            }

            formData.append("file", file);
            setRunningSubmission(true);
            var accessToken = await globalContextRef.current.getAccessTokenAsync();
            var url = await MiscService.uploadFormFileAsync(accessToken, formData);
            setRunningSubmission(false);

            if (url) {
                setPhoto(URL.createObjectURL(file));
                setPhotoUrl(url);
            }
            else {
                alert("The file could not be attached.");
            }
        }
    };

    return (
        <>
            <input
                type='file'
                id='attachPhotoRef'
                ref={attachPhotoRef}
                onChange={attachPhotoCompleteAsync}
                style={{ display: 'none' }} />

            {
                mode === "idle" &&
                <>
                    <Row>
                        <Col>
                            <h2>Choose a Mode</h2>
                            <Button
                                className="m-3"
                                variant="success"
                                style={{
                                    width: "160px",
                                    height: "160px",
                                    fontSize: "20px"
                                }}
                                onClick={() => setMode("qa")}>QA</Button>

                            <Button
                                className="m-3"
                                variant="success"
                                style={{
                                    width: "160px",
                                    height: "160px",
                                    fontSize: "20px"
                                }}
                                onClick={() => setMode("receive")}>Receive</Button>

                            <Button
                                className="m-3"
                                variant="success"
                                style={{
                                    width: "160px",
                                    height: "160px",
                                    fontSize: "20px"
                                }}
                                onClick={() => setMode("ship")}>Ship</Button>
                        </Col>
                    </Row>
                </>
            }

            {
                mode !== "idle" &&
                submissionQueue.length < 1 &&
                <>
                    <Row>
                        <h1
                            className="text-center p-3"
                            onClick={() => setMode("idle")}
                            style={{
                                backgroundColor: "rgba(0,0,0,0.2)",
                                cursor: "pointer"
                            }}>Equipment {
                                mode === "qa" ? "QA" :
                                    mode === "receive" ? "Receiving" :
                                        mode === "ship" ? "Shipping" :
                                            "Unknown"
                            }</h1>
                        <Col sm={6}>
                            <h2>Default Values</h2>
                            <span style={{ fontSize: "1.0rem", lineHeight: "0" }}><i>If you are updating existing equipment: Any info provided below will override it's current info.</i></span>


                            <DPSEntitySelector<IFireDepartment, IFireDepartmentFilters>
                                entityFilterSearchPropName={"fireDeptNames"}
                                entityService={FireDepartmentService}
                                entityTypeName="Department"
                                defaultEntityFilter={{}}
                                selectedEntityRef={fireDepartment}
                                generateEntityTitle={(entry) => `${entry.fireDeptName} ${entry.hqcity} ${entry.hqstate}, ${entry.hqzip}`}
                                onClickExternalHandler={(entry) => {
                                    if (entry) {
                                    }
                                }}
                                entityChangeHandler={(newEntry) => {
                                    setFireDepartment(newEntry);
                                }}
                            />

                            <DPSEntitySelector<IDealer, IDealerFilters>
                                entityFilterSearchPropName={"dealerNames"}
                                entityService={DealerService}
                                entityTypeName="Dealer"
                                defaultEntityFilter={{}}
                                selectedEntityRef={dealer}
                                generateEntityTitle={(entry) => entry.dealerName ?? ""}
                                onClickExternalHandler={(entry) => {
                                    if (entry) {
                                    }
                                }}
                                entityChangeHandler={(newEntry) => {
                                    setDealer(newEntry);
                                }}
                            />

                            <DPSEntitySelector<ISalesRep, ISalesRepFilters>
                                entityFilterSearchPropName={"names"}
                                entityService={SalesRepService}
                                entityTypeName="Rep"
                                defaultEntityFilter={{ dealerIds: dealer ? [dealer!.dealerId] : undefined }}
                                selectedEntityRef={salesRep}
                                generateEntityTitle={(entry) => entry?.fullName ?? ""}
                                onClickExternalHandler={(entry) => {
                                    if (entry) {
                                    }
                                }}
                                entityChangeHandler={(newEntry) => {
                                    setSalesRep(newEntry);
                                }}
                            />

                            <Card className="mb-3 mt-3">
                                <Card.Body>
                                    <Card.Title>
                                        Providing these will register all equipment as well.
                                    </Card.Title>
                                    <Form.Control
                                        type="text"
                                        placeholder="Registration Name"
                                        className="mt-2"
                                        onChange={
                                            (e: React.ChangeEvent<HTMLInputElement>) => {
                                                setRegistrationName(e.target.value);
                                            }
                                        }
                                        value={registrationName}
                                    />

                                    <Form.Control
                                        type="text"
                                        placeholder="Registration Email"
                                        className="mt-3"
                                        onChange={
                                            (e: React.ChangeEvent<HTMLInputElement>) => {
                                                setRegistrationEmail(e.target.value);
                                            }
                                        }
                                        value={registrationEmail}
                                    />


                                </Card.Body>
                            </Card>
                            <Form.Control
                                as="textarea"
                                rows={10}
                                className="mt-3"
                                placeholder="Serials"
                                onChange={
                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                        // @ts-ignore
                                        setSerials((e.target.value ?? "").split("\n"));
                                    }
                                }
                                value={(serials ?? []).join("\n")}
                            />
                        </Col>
                        <Col>
                            <h2>Equipment</h2>
                            <QRCodeReader
                                onScanSuccess={onEquipmentScanSuccess}
                            />

                            <Button
                                variant="warning"
                                className="mb-3"
                                style={{
                                    width: "100%",
                                    height: "60px",
                                    fontSize: "20px"
                                }}
                                onClick={startPhotoSubmission}>Attach photo</Button>

                            <Button
                                variant="success"
                                className="mb-3"
                                style={{
                                    width: "100%",
                                    height: "60px",
                                    fontSize: "20px"
                                }}
                                onClick={doSubissionsAsync}>Update {serials.filter(s => !!s)?.length ?? "0"} Serial(s)</Button>

                            <p><i>"Attach photo to equipment" will attach the photo you choose to all serials provided!</i></p>

                            {
                                photo &&
                                <img src={photo} style={{ width: "100%" }} />
                            }
                        </Col>
                    </Row>
                </>
            }

            {
                mode !== "idle" &&
                submissionQueue.length > 0 &&
                <>
                    <Row>
                        <h1
                            className="text-center p-3"
                            style={{
                                backgroundColor: "rgba(0,255,0,0.2)"
                            }}>Marking Equipment as {
                                mode === "qa" ? "QA Passed" :
                                    mode === "receive" ? "Received" :
                                        mode === "ship" ? "Shipped" :
                                            "Unknown"
                            }</h1>
                        <Col>
                            {
                                submissionQueue.map(
                                    (e, index) => {
                                        return (
                                            <div
                                                key={index}
                                                style={{
                                                    fontSize: "25px",
                                                    listStyleType: "none",
                                                    fontWeight: e.message ? "bold" : "inherit",
                                                    backgroundColor: e.message ? "rgba(255,0,0,0.2)" : "inherit",
                                                    padding: "12px"
                                                }}>
                                                {
                                                    e.state === "idle" ?
                                                        <></> :
                                                        e.state === "submitting" ?
                                                            <FontAwesomeIcon icon={faArrowsSpin as IconProp} /> :
                                                            e.state === "success" ?
                                                                <FontAwesomeIcon icon={faCheck as IconProp} /> :
                                                                e.state === "error" ?
                                                                    <FontAwesomeIcon icon={faWarning as IconProp} /> :
                                                                    <></>
                                                } {e.serial}{e.message ? ` - ${e.message}` : ""}
                                            </div>
                                        );
                                    })
                            }
                        </Col>
                    </Row>
                    {
                        !runningSubmission &&
                        <Row>
                            <Col>
                                <Button
                                    className="m-3"
                                    variant="success"
                                    style={{
                                        width: "100%",
                                        height: "100px",
                                        fontSize: "20px"
                                    }}
                                    onClick={() => resetAll()}>Reset</Button>
                            </Col>
                        </Row>
                    }
                </>
            }
        </>
    );
}

export default EquipmentUpdate;
